import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, map, Observable, tap } from 'rxjs';

import { PortalLeadService } from '@app/services/portal-lead/portal-lead.service';
import { SessionProceedCoordinator } from '@app/services/session-proceed-coordinator.service';
import { buildCfarSignInRedirectExtras } from '@app/utils/portal-sign-in-extras.util';

import { Steps } from '@app/components/stepper/stepper.component';
import {
  AncillaryTypeEnum,
  AncillaryUpsellNode,
  PortalBundledAncillary,
  PortalOrderNode,
} from '@app/services/api/api.types';
import { PortalOrderService } from '@app/services/portal-order/portal-order.service';
import { StateService } from '@app/services/state/state.service';
import { Tracking } from '@app/services/tracking/tracking.service';
import { PortalTrackingEvents } from '@app/utils/tracking-events';
import { PortalRoutes } from '@app/app-routing.module';
import { PopupsService } from '@voyagu/portal-design-system';
import { PortalCurrencyPipe } from '@app/pipes/portal-currency.pipe';

@Component({
  selector: 'cancel-for-any-reason-page',
  templateUrl: './cancel-for-any-reason-page.component.html',
  styleUrl: './cancel-for-any-reason-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SessionProceedCoordinator],
})
export class CancelForAnyReasonPageComponent implements OnInit {
  Steps = Steps;

  portalOrderService = inject(PortalOrderService);
  portalLeadService = inject(PortalLeadService);
  stateService = inject(StateService);
  location = inject(Location);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  popupsService = inject(PopupsService);
  portalCurrencyPipe = inject(PortalCurrencyPipe);
  sessionProceed = inject(SessionProceedCoordinator);

  portalOrder$: Observable<PortalOrderNode | null>;
  portalOrder: PortalOrderNode | null;

  disruptionProtectionAncillary?: PortalBundledAncillary;

  sections: { title: string; items: { icon: string; label: string; details: string }[] }[] = [];

  ancillaryUpsell: boolean;

  isNewUser = false;

  priceWithoutDiscountPerTraveler = 0;
  pricePerTraveler = 0;
  price = 0;

  @ViewChild('disruptionProtectionModalTemplate')
  disruptionProtectionModalTemplate: TemplateRef<unknown>;
  @ViewChild('ancillariesBundleModalTemplate') ancillariesBundleModalTemplate: TemplateRef<unknown>;
  @ViewChild('askToSignInTemplate') askToSignInTemplate: TemplateRef<unknown>;

  ngOnInit() {
    this.ancillaryUpsell = this.activatedRoute.snapshot.data['ancillaryUpsell'] as boolean;

    Tracking.track(PortalTrackingEvents.portalCancelForAnyReasonOpened);

    this.portalOrder$ = combineLatest([
      this.portalOrderService.portalOrder$,
      this.portalLeadService.portalLead$,
    ]).pipe(
      tap(([portalOrder, portalLead]) => {
        this.isNewUser = !!portalLead?.isNewUser;

        if (portalOrder) {
          this.portalOrder = portalOrder;

          const cancelForAnyReasonAncillaryBundle = portalOrder.ancillaryBundle?.ancillaries.find(
            (ancillary) => ancillary.type === AncillaryTypeEnum.CANCEL_FOR_ANY_REASON,
          );

          const cancelForAnyReasonAncillary = portalOrder.ancillaries?.find(
            (ancillary) => ancillary.type === AncillaryTypeEnum.CANCEL_FOR_ANY_REASON,
          );

          if (cancelForAnyReasonAncillaryBundle) {
            this.priceWithoutDiscountPerTraveler =
              cancelForAnyReasonAncillaryBundle.priceWithoutDiscountPerTraveler || 0;
            this.pricePerTraveler = cancelForAnyReasonAncillaryBundle.pricePerTraveler || 0;
            this.price = cancelForAnyReasonAncillaryBundle.priceWithoutDiscount || 0;
          } else if (cancelForAnyReasonAncillary) {
            this.priceWithoutDiscountPerTraveler =
              cancelForAnyReasonAncillary.amountPerTraveler || 0;
            this.pricePerTraveler = cancelForAnyReasonAncillary.amountPerTraveler || 0;
            this.price = cancelForAnyReasonAncillary.price || 0;
          }

          this.disruptionProtectionAncillary = portalOrder.ancillaryBundle?.ancillaries.find(
            (ancillary) => ancillary.type === AncillaryTypeEnum.DISRUPTION_PROTECTION,
          );

          this.sections = [
            {
              title: 'If your plans change, we will:',
              items: [
                {
                  icon: 'travel_green',
                  label: 'Cancel for any reason',
                  details: 'No explanations required',
                },
                {
                  icon: 'money_green',
                  label: 'Get your money back',
                  details: `100% travel credit (${this.portalCurrencyPipe.transform(this.portalOrder.soldAmountPerAdult)} per traveler) or 80% cash refund (${this.portalCurrencyPipe.transform(this.portalOrder.soldAmountPerAdult * 0.8)} per traveler) — your choice`,
                },
                {
                  icon: 'support',
                  label: 'Avoid cancellation fees',
                  details: 'No airline penalties or hidden charges',
                },
                {
                  icon: 'thumbs_up',
                  label: 'Stay flexible until the last moment',
                  details: 'Change your plans without financial stress',
                },
              ],
            },
            {
              title: 'Without Cancel For Any Reason, you may need:',
              items: [
                {
                  icon: 'travel_orange',
                  label: 'Forfeit the ticket value',
                  details: "Most airline's tickets are non-refundable",
                },
                {
                  icon: 'plane_orange',
                  label: 'Pay hefty cancellation fees',
                  details: 'If allowed to cancel, then cancelation fees apply',
                },
                {
                  icon: 'travel_orange',
                  label: 'Fly restrictively with airline credit',
                  details:
                    'Airline credits force you to fly the same airline, passenger and routes',
                },
              ],
            },
          ];
        }
      }),
      map(([portalOrder]) => portalOrder),
    );
  }

  back() {
    this.location.back();

    Tracking.track(PortalTrackingEvents.portalCancelForAnyReasonBackPressed);
  }

  cfarContinueWouldRedirectToTravelers(): boolean {
    if (this.ancillaryUpsell) {
      return false;
    }

    const disruptionProtection = this.stateService.getState().disruptionProtection;

    if (disruptionProtection) {
      return true;
    }

    if (this.disruptionProtectionAncillary) {
      return false;
    }

    return true;
  }

  cfarNextWouldRedirectToTravelers(): boolean {
    if (this.ancillaryUpsell) {
      return false;
    }

    return !!this.stateService.getState().disruptionProtection;
  }

  continue() {
    Tracking.track(PortalTrackingEvents.portalCancelForAnyReasonContinuePressed);

    this.sessionProceed.runAfterSessionCheckIf(
      this.cfarContinueWouldRedirectToTravelers(),
      this.isNewUser,
      () => {
        const cancelForAnyReason = Number(this.price) || 0;

        this.stateService.patchState({ cancelForAnyReason });

        const disruptionProtection = this.stateService.getState().disruptionProtection;

        if (disruptionProtection) {
          this.stateService.patchState({
            disruptionProtection: undefined,
            cancelForAnyReason: undefined,
            bundle: this.portalOrder?.ancillaryBundle,
          });

          this.redirect();
        } else if (this.disruptionProtectionAncillary) {
          this.popupsService.showModal(this.disruptionProtectionModalTemplate);
        } else {
          this.redirect();
        }
      },
      () => this.showAskToSignInModal(),
    );
  }

  next() {
    Tracking.track(PortalTrackingEvents.portalCancelForAnyReasonNoThanksPressed);

    this.sessionProceed.runAfterSessionCheckIf(
      this.cfarNextWouldRedirectToTravelers(),
      this.isNewUser,
      () => {
        const cancelForAnyReason = undefined;
        this.stateService.patchState({ cancelForAnyReason });

        const disruptionProtection = this.stateService.getState().disruptionProtection;

        if (disruptionProtection) {
          this.redirect();
        } else {
          this.popupsService.showModal(this.ancillariesBundleModalTemplate);
        }
      },
      () => this.showAskToSignInModal(),
    );
  }

  showAskToSignInModal() {
    this.popupsService.showModal(this.askToSignInTemplate);
    this.sessionProceed.trackSignInFromCheckoutPromptOpened();
  }

  hideAskToSignInModal() {
    this.sessionProceed.dismissAskModal(() => this.popupsService.hideLast());
  }

  continueAsGuestFromSignInPrompt() {
    this.sessionProceed.continueAsGuestAfterPrompt(() => this.popupsService.hideLast());
  }

  showSignInModal() {
    const upsell = this.ancillaryUpsell
      ? (this.portalOrder as unknown as AncillaryUpsellNode)
      : null;

    const extras = buildCfarSignInRedirectExtras({
      state: this.stateService.getState(),
      portalOrder: this.portalOrder,
      cfarPrice: Number(this.price) || 0,
      ancillaryUpsell: this.ancillaryUpsell,
      upsell,
      upsellTripPortalLink:
        this.activatedRoute.snapshot.paramMap.get(PortalRoutes.AncillaryUpsellParam) ?? undefined,
    });

    this.sessionProceed.openSignInModalForPortalOption(
      this.stateService.getState().portalLink,
      this.portalOrder?.portalLink ?? '',
      extras,
    );
  }

  getSubmitNextSegment() {
    const afterBundleSkipsTravelers = this.ancillaryUpsell;

    if (this.portalOrder?.isChange) {
      return PortalRoutes.Gratuity;
    }

    if (afterBundleSkipsTravelers) {
      const upsell = this.portalOrder as unknown as AncillaryUpsellNode;
      return upsell.showGratuity ? PortalRoutes.Gratuity : PortalRoutes.OverviewPayment;
    }

    return PortalRoutes.Travelers;
  }

  redirect() {
    const nextSegment = this.getSubmitNextSegment();

    const path = this.ancillaryUpsell
      ? [
          PortalRoutes.AncillaryUpsell,
          this.activatedRoute.snapshot.paramMap.get(PortalRoutes.AncillaryUpsellParam),
          nextSegment,
        ]
      : [this.stateService.getState().portalLink, this.portalOrder?.portalLink, nextSegment];

    void this.router.navigate(path, { queryParamsHandling: 'merge' });
  }

  /**
   * Matches `cfarContinueWouldRedirectToTravelers` / `cfarNextWouldRedirectToTravelers`: upsell
   * never opens ask-to-sign-in on this step; option flow to Travelers does.
   */
  optionCheckoutRequiresTravelersSignInPrompt() {
    return !this.ancillaryUpsell;
  }

  showAskToSignInAfterClosingModal() {
    this.popupsService.hideLast();
    this.showAskToSignInModal();
  }

  applyFullBundleFromOrderAndRedirect() {
    this.stateService.patchState({
      disruptionProtection: undefined,
      cancelForAnyReason: undefined,
      bundle: this.portalOrder?.ancillaryBundle,
    });
    this.popupsService.hideLast();
    this.redirect();
  }

  closeModalAndRedirect() {
    this.popupsService.hideLast();
    this.redirect();
  }

  runModalActionWithSessionGate(proceed: () => void) {
    this.sessionProceed.runAfterSessionCheckIf(
      this.optionCheckoutRequiresTravelersSignInPrompt(),
      this.isNewUser,
      proceed,
      () => this.showAskToSignInAfterClosingModal(),
    );
  }

  upsellDisruptionProtectionContinue() {
    Tracking.track(PortalTrackingEvents.portalUpsellDisruptionProtectionModalContinuePressed);

    this.runModalActionWithSessionGate(() => this.applyFullBundleFromOrderAndRedirect());
  }

  upsellDisruptionProtectionNext() {
    Tracking.track(PortalTrackingEvents.portalUpsellDisruptionProtectionModalNoThanksPressed);

    this.runModalActionWithSessionGate(() => this.closeModalAndRedirect());
  }

  ancillariesBundleContinue() {
    Tracking.track(PortalTrackingEvents.portalUpsellBundleModalContinuePressed);

    this.runModalActionWithSessionGate(() => this.applyFullBundleFromOrderAndRedirect());
  }

  ancillariesBundleNext() {
    Tracking.track(PortalTrackingEvents.portalUpsellBundleModalNoThanksPressed);

    this.runModalActionWithSessionGate(() => this.closeModalAndRedirect());
  }

  hideModal() {
    this.popupsService.hideLast();
  }
}
